import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { debounce } from '@mui/material';

import { MultiSelect } from 'common/components/FormField';
import { InputOptionSkillsDocument } from 'graphql/types';
import { TInputOption } from 'common/types';

interface IProps {
    isFilter?: boolean;
    label?: string;
    ids?: string[];
    onChange?: (name: string, value: string[]) => void;
}

const BASE_QUERY_VARS = {
    first: 50,
};

export const SkillSelectField = ({
    isFilter,
    label,
    ids = [],
    onChange,
    ...other
}: IProps & FieldProps) => {
    const [inputValue, setInputValue] = useState('');
    const [translate] = useTranslation();

    const [fetchSkills, { data, loading, called }] = useLazyQuery(
        InputOptionSkillsDocument
    );

    const { skills: skillsData } = data || {};

    const skills =
        skillsData?.edges.map((edge) => edge?.node).filter(Boolean) || [];
    const skillsCount = skillsData?.count || 0;

    const inputOptions: TInputOption[] = useMemo(
        () =>
            skills.map(({ id, name }) => ({
                id,
                name: name,
                extraCategoryLabels: [],
                isInfo: false,
            })) || [],
        [skills]
    );

    const {
        form: { setFieldValue },
        field,
    } = other;

    const { name, value: fieldValue } = field;

    const value: TInputOption[] = fieldValue || [];

    // Add value options to input options if they are not already in the input options
    value.forEach((valueOption) => {
        if (
            !inputOptions.some(
                (option: TInputOption) => option.id === valueOption.id
            )
        ) {
            const { id, name, extraCategoryLabels } = valueOption;

            inputOptions.push({
                id,
                name,
                extraCategoryLabels,
                isInfo: false,
            });
        }
    });

    if (skillsCount > BASE_QUERY_VARS.first) {
        inputOptions.push({
            id: '',
            name: translate('moreResultsSelectSearch'),
            extraCategoryLabels: [],
            isInfo: true,
        });
    }

    const handleChange = (name: string, value: string[]) => {
        onChange?.(name, value);
        setFieldValue(name, value);
    };

    const fetch = useMemo(
        () =>
            debounce((inputValue: string) => {
                fetchSkills({
                    variables: {
                        ...BASE_QUERY_VARS,
                        q: inputValue || undefined,
                    },
                });
            }, 400),
        []
    );

    // Searching for results
    useEffect(() => {
        // The first query is done by opening, so we want to avoid calling it twice
        if (!called) return;

        fetch(inputValue);
    }, [inputValue, fetch]);

    // Fetch skills when ids change
    useEffect(() => {
        if (!ids.length || !called) return;

        fetchSkills({
            variables: {
                ...BASE_QUERY_VARS,
            },
        });
    }, [ids]);

    const handleOpen = () => {
        if (!!skills.length) return;

        fetchSkills({
            variables: {
                ...BASE_QUERY_VARS,
                q: inputValue || undefined,
            },
        });
    };

    return (
        <Field
            component={MultiSelect}
            filterOptions={(option: TInputOption) => option}
            getOptionDisplay={(option: TInputOption) => <>{option.name}</>}
            getOptionIsInfo={(option: TInputOption) => option.isInfo}
            getOptionKey={(option: TInputOption) => option.id}
            getOptionLabel={(option: TInputOption) => option.name}
            InputProps={{ label }}
            isOptionEqualToValue={(option: TInputOption, value: TInputOption) =>
                option.id === value.id
            }
            limitTags={5}
            loading={loading}
            loadingText={translate('loadingDots')}
            name={name || 'skills'}
            noOptionsText={translate(
                `noOptionsText.${isFilter ? 'filterScorms' : 'filterSkills'}`
            )}
            options={inputOptions}
            placeholder={translate('selectSkill')}
            onChange={handleChange}
            onInputChange={(
                _event: React.ChangeEvent<HTMLInputElement>,
                newInputValue: string
            ) => {
                setInputValue(newInputValue);
            }}
            onOpen={handleOpen}
            {...other}
        />
    );
};
